<template>
  <div>
    <page-heading title="Timesheets"></page-heading>

    <el-card>
      <div slot="header">All Timesheets</div>
      <div slot="actions" class="timesheet-actions">
        <el-select v-model="whichYear" placeholder="Filter by Year" @change="handleFilterChange">
          <el-option
            v-for="item in years"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
        <el-select v-model="week" placeholder="Filter by Week" @change="handleFilterChange">
          <el-option
            v-for="item in weeks"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
        <el-select v-model="userId" placeholder="Filter by Employee" @change="handleFilterChange">
          <el-option
            v-for="item in users"
            :key="item.id"
            :label="item.profile.full_name"
            :value="item.id">
          </el-option>
        </el-select>
        <el-select v-model="status" placeholder="Filter by Status (default: Pending)" @change="handleFilterChange">
          <el-option
            v-for="item in statuses"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
        <el-button class="top-padded-action" :loading="syncSubmitting" @click="confirmBmsSync" type="success">
          Sync Kaseya BMS (Selected Week)
        </el-button>
        <el-button class="top-padded-action action-gap-right" @click="handleReset" type="primary">Reset</el-button>
      </div>

      <content-placeholder v-if="fetching">
        <el-card>
          <p class="text text-10 text-h-20"></p>
          <p class="text text-full"></p>
          <p class="text text-full"></p>
          <p class="text text-50"></p>
          <p class="text text-25"></p>
        </el-card>
      </content-placeholder>

      <div v-else>
        <el-alert
          v-if="syncStatus"
          :title="syncStatusTitle"
          :description="syncStatusDescription"
          :type="syncStatusType"
          :closable="false"
          show-icon>
        </el-alert>

        <div v-if="timesheets.length > 0">

          <el-table
            stripe
            :data="timesheets"
            style="width: 100%"
            :border="true"
            :default-sort = "{ prop: 'status', order: 'ascending' }"
            @filter-change="handleFilterChange"
            @sort-change="handleSortChange"
            class="depth">

            <el-table-column
              prop="status"
              label="Status"
              align="center"
              width="110">
              <template slot-scope="scope">
                <el-tag size="small" :type="setType(scope.row.status)" effect="dark">{{ setValue(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>

            <el-table-column
              prop='week'
              label="Week #"
              align="center"
              width="75">
              <template slot-scope="scope">
                {{ scope.row.week }}
              </template>
            </el-table-column>

            <el-table-column
              label="Year"
              align="center"
              width="75">
              <template slot-scope="scope">
                {{ scope.row.year }}
              </template>
            </el-table-column>

            <el-table-column
              label="Name/Email">
              <template slot-scope="scope">
                <span v-if="scope.row.user.profile">
                  {{ scope.row.user.profile.first_name }}
                  {{ scope.row.user.profile.last_name }}
                </span>
                <span>
                   [ {{ scope.row.user.email}} ]
                </span>
              </template>
            </el-table-column>

            <el-table-column
              label="Comments"
              >
              <template slot-scope="scope">

              </template>
            </el-table-column>

            <el-table-column
              label="Submitted"
              align="right"
              width="250">
              <template slot-scope="scope">
                {{ scope.row.created_at | formatTime('MMMM DD, YYYY hh:mm a') }}
              </template>
            </el-table-column>

            <el-table-column
              label=""
              width="120" align="center">
              <template slot-scope="scope">
                <router-link  :to="{ name: 'timesheet', params: { id: scope.row.id } }"><el-button type="primary" size="mini"><i class="fas fa-search"></i></el-button></router-link>
              </template>
            </el-table-column>

          </el-table>
        </div>
        <div v-else>
          <el-alert type="info" :closable="false" title="There are no timesheets entries!" center></el-alert>
        </div>
      </div>

      <div slot="footer">
        <content-placeholder v-if="fetching">
          <el-card>
            <p class="text text-24"></p>
          </el-card>
        </content-placeholder>
        <div class="pagination" v-else>
          <el-pagination
            @current-change="handleCurrentChange"
            :current-page.sync="currentPage"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="total">
          </el-pagination>
        </div>
      </div>
    </el-card>

  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import moment from 'moment';
  import { formatTime } from '@/filters/time';
  import { showSuccessMessage, showErrorMessage, showWarningMessage } from '@/ui/message';

  const BMS_TERMINAL_STATUSES = ['success', 'failed', 'partial_success'];

  export default {
    name: 'TimesheetsPage',
    created() {
      this.fetchData();
    },
    data() {
      const endYear = moment().year();
      const yearSelection = [];
      for (let i = 2016; i < endYear + 1; i += 1) {
        const obj = {
          label: i,
          value: i,
        };
        yearSelection.push(obj);
      }

      return {
        whichYear: moment().year(),
        userId: null,
        status: 'pending',
        week: null,
        years: yearSelection,
        weeks: Array.from({ length: 52 }, (_, i) => i + 1).map(x => Object.create({ label: x, value: x })),
        statuses: [
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'resubmitted', label: 'Re-Submitted' },
          { value: 'archived', label: 'Archived' },
        ],
        loading: true,
        reloading: false,
        loadError: null,
        currentPage: 1,
        pageSize: 20,
        total: 0,
        timesheets: [],
        sortColumn: 'status',
        sortOrder: 'asc',
        filters: {},
        fetching: false,
        syncSubmitting: false,
        syncPolling: false,
        syncStatus: null,
        syncPollTimer: null,
      };
    },
    beforeDestroy() {
      this.stopSyncPolling();
    },
    computed: {
      users() {
        return this.$store.getters['entities/users/query']()
          .where(record => !record.admin && !record.deactivated)
          .with('profile')
          .with('user_memberships')
          .get();
      },
      syncStatusType() {
        if (!this.syncStatus) {
          return 'info';
        }

        switch (this.syncStatus.status) {
          case 'success':
            return 'success';
          case 'partial_success':
            return 'warning';
          case 'failed':
            return 'error';
          case 'queued':
          case 'running':
            return 'info';
          default:
            return 'info';
        }
      },
      syncStatusTitle() {
        if (!this.syncStatus) {
          return '';
        }

        const baseStatus = (this.syncStatus.status || '').replace('_', ' ');
        const statusLabel = baseStatus ? baseStatus.toUpperCase() : 'UNKNOWN';
        return `Kaseya Sync Status: ${statusLabel}`;
      },
      syncStatusDescription() {
        if (!this.syncStatus) {
          return '';
        }

        const parts = [];
        if (this.syncStatus.job_id) {
          parts.push(`Job: ${this.syncStatus.job_id}`);
        }
        if (typeof this.syncStatus.attempts === 'number') {
          parts.push(`Attempts: ${this.syncStatus.attempts}`);
        }
        if (typeof this.syncStatus.synced_timesheets === 'number') {
          parts.push(`Synced: ${this.syncStatus.synced_timesheets}`);
        }
        if (typeof this.syncStatus.failed_timesheets === 'number') {
          parts.push(`Failed: ${this.syncStatus.failed_timesheets}`);
        }
        if (this.syncStatus.last_error) {
          parts.push(`Error: ${this.syncStatus.last_error}`);
        }
        if (this.syncStatus.started_at) {
          parts.push(`Started: ${formatTime(this.syncStatus.started_at, 'MMMM DD, YYYY hh:mm a')}`);
        }
        if (this.syncStatus.finished_at) {
          parts.push(`Finished: ${formatTime(this.syncStatus.finished_at, 'MMMM DD, YYYY hh:mm a')}`);
        }

        return parts.join(' | ');
      },
    },
    methods: {
      ...mapActions('entities/timesheets', ['fetchTimesheets', 'triggerBmsSync', 'fetchBmsSyncStatus']),

      buildParams() {
        const params = {
          page: this.currentPage,
          per_page: this.pageSize,
          sort_field: this.sortColumn,
          sort_order: this.sortOrder,
          year: this.whichYear,
          week: this.week,
          user_id: this.userId,
          status: this.status,
          ...this.filters,
        };
        return params;
      },

      buildSyncPayload() {
        return {
          week: this.week,
          year: this.whichYear,
          force: false,
          force_clear: false,
          user_id: this.userId || undefined,
        };
      },

      getSyncScopeLabel() {
        const userScope = this.userId ? `user ${this.userId}` : 'all users';
        return `week ${this.week}, year ${this.whichYear}, ${userScope}`;
      },

      getErrorCode(error) {
        if (error && typeof error === 'object' && error.error_code) {
          return error.error_code;
        }
        return null;
      },

      getErrorMessage(error, fallback = 'Unable to complete request.') {
        if (!error) {
          return fallback;
        }
        if (typeof error === 'string') {
          return error;
        }
        if (Array.isArray(error)) {
          return error.join(', ');
        }
        if (error.message) {
          return error.message;
        }
        return fallback;
      },

      stopSyncPolling() {
        if (this.syncPollTimer) {
          clearInterval(this.syncPollTimer);
          this.syncPollTimer = null;
        }
        this.syncPolling = false;
      },

      async pollSyncStatus(jobId) {
        const response = await this.fetchBmsSyncStatus({ jobId });
        if (!response.status) {
          const message = this.getErrorMessage(response.error, 'Failed to retrieve sync status.');
          showWarningMessage(message);
          this.stopSyncPolling();
          return;
        }

        this.syncStatus = response.data;
        if (BMS_TERMINAL_STATUSES.includes(this.syncStatus.status)) {
          this.stopSyncPolling();

          if (this.syncStatus.status === 'failed') {
            const errorMsg = this.syncStatus.last_error || 'Kaseya sync failed.';
            showErrorMessage(errorMsg);
          } else if (this.syncStatus.status === 'partial_success') {
            showWarningMessage('Kaseya sync completed with partial success. Review failed entries.');
          } else {
            showSuccessMessage('Kaseya sync completed successfully.');
          }

          this.fetchData();
        }
      },

      startSyncPolling(jobId) {
        this.stopSyncPolling();
        this.syncPolling = true;

        // Kick off an immediate status check, then poll every 3 seconds.
        this.pollSyncStatus(jobId);
        this.syncPollTimer = setInterval(() => {
          this.pollSyncStatus(jobId);
        }, 3000);
      },

      async confirmBmsSync() {
        if (!this.week || !this.whichYear) {
          showErrorMessage('Select week and year before running Kaseya sync.');
          return;
        }

        const scopeLabel = this.getSyncScopeLabel();
        try {
          await this.$confirm(`Run Kaseya sync for ${scopeLabel}?`, 'Confirm Kaseya Sync', {
            confirmButtonText: 'Run Sync',
            cancelButtonText: 'Cancel',
            type: 'warning',
          });
        } catch (e) {
          return;
        }

        this.syncSubmitting = true;
        const response = await this.triggerBmsSync(this.buildSyncPayload());
        this.syncSubmitting = false;

        if (!response.status) {
          const errorCode = this.getErrorCode(response.error);
          if (errorCode === 'conflict_inflight_job') {
            showWarningMessage(this.getErrorMessage(response.error, 'A matching sync job is already running.'));
            return;
          }

          showErrorMessage(this.getErrorMessage(response.error, 'Unable to start Kaseya sync.'));
          return;
        }

        this.syncStatus = response.data;

        if (response.data.deduped) {
          showWarningMessage('A matching sync is already in progress. Subscribed to existing job status.');
        } else {
          showSuccessMessage('Kaseya sync job queued. Tracking progress now.');
        }

        this.startSyncPolling(response.data.job_id);
      },

      fetchData() {
        this.showLoader();
        this.fetching = true;
        this.loadError = null;
        this.fetchTimesheets(this.buildParams())
          .then((response) => {
            this.fetching = false;
            if (response.status) {
              this.total = response.data.meta.total_entries;
              this.timesheets = response.data.entities;
              // enforce a predictable status priority so "pending" appears first
              const statusOrder = { pending: 0, resubmitted: 1, approved: 2, rejected: 3, archived: 4 };
              this.timesheets.sort((a, b) => {
                const statusCompare = (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
                if (statusCompare !== 0) {
                  return statusCompare;
                }
                if (a.year !== b.year) {
                  return (b.year || 0) - (a.year || 0);
                }
                return (a.week || 0) - (b.week || 0);
              });
            } else {
              this.loadError = response.error;
            }
            this.hideLoader();
          });
      },

      actionFormatter(row, column) {
        const property = column.property;
        return this.$t(`modules.timesheet.actions.${row[property]}`);
      },

      dateFormatter(row, column) {
        const property = column.property;
        return formatTime(row[property], 'MMMM DD, YYYY hh:mm a');
      },

      handleCurrentChange(val) {
        this.currentPage = val;
        this.fetchData();
      },

      handleSortChange({ prop, order }) {
        this.sortColumn = prop;
        this.sortOrder = order === 'descending' ? 'desc' : 'asc';
        this.fetchData();
      },

      handleFilterChange(filters) {
        this.currentPage = 1;
        this.filters = filters;
        this.fetchData();
      },

      handleReset() {
        this.stopSyncPolling();
        this.syncStatus = null;
        Object.assign(this.$data, this.$options.data.apply(this));
        this.fetchData();
      },

      showLoader() {
        if (this.timesheets.length) {
          this.reloading = true;
        } else {
          this.loading = true;
        }
      },

      hideLoader() {
        this.reloading = false;
        this.loading = false;
      },

      setType(status) {
        switch (status) {
          case 'approved':
            return 'success';
          case 'rejected':
            return 'danger';
          case 'pending':
            return 'warning';
          default:
            return 'info';
        }
      },
      setValue(status) {
        switch (status) {
          case 'approved':
            return 'Approved';
          case 'rejected':
            return 'Rejected';
          case 'pending':
            return 'Pending';
          default:
            return 'Not Submitted';
        }
      },
    },
  };
</script>

<style lang="scss">
  .el-tabs__content{
    overflow: visible;
  }

  .timesheet-actions {
    .el-button {
      float: none;
    }

    .top-padded-action {
      margin-top: 8px;
    }
  }

  .action-gap-right {
    margin-right: 8px;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Timesheets.vue